import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { Scale, Camera, TrendingUp, Heart, History, LogOut, Sparkles, UserCog, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguagePicker from "@/components/LanguagePicker";
import { DashboardCard } from "@/components/DashboardCard";

const CARDS = [
  { key: "imc", icon: Scale, path: "/imc" },
  { key: "photos", icon: Camera, path: "/photos", color: "from-yellow-900/40 to-yellow-800/20", border: "border-primary/50", highlight: true },
  { key: "progress", icon: TrendingUp, path: "/progress" },
  { key: "history", icon: History, path: "/history" },
  { key: "treatments", icon: Heart, path: "/treatments" },
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [logoutError, setLogoutError] = useState("");
  const handleLogout = async () => {
    try { await logout(); } catch (error) { setLogoutError(error.message); }
  };

  const firstName = user?.full_name?.split(" ")[0] || t("dashboard.fallbackName");
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-bold text-primary tracking-wide">{t("common.appName")}</span>
          </div>
          <div className="flex items-center gap-1">
            <LanguagePicker />
            <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" data-testid="dashboard-account-btn">
              <Link to="/account" aria-label={t("account.title")}><UserCog className="w-4 h-4" /></Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1" />
              {t("common.exit")}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {logoutError && <p role="alert" data-testid="logout-error" className="text-destructive mb-4">{logoutError}</p>}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">{t("dashboard.hello", { name: firstName })}</h1>
          <p className="text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
        </div>

        <div className="space-y-3">
          {isAdmin && (
            <DashboardCard to="/admin" testId="dashboard-card-admin" icon={BarChart3} title={t("admin.title")} description={t("admin.subtitle")}
              color="from-primary/20 to-primary/5" border="border-primary/50" highlight />
          )}
          {CARDS.map((card) => (
            <DashboardCard key={card.path} to={card.path} testId={`dashboard-card-${card.path.slice(1)}`} icon={card.icon}
              title={t(`dashboard.cards.${card.key}`)} description={t(`dashboard.cards.${card.key}Desc`)}
              color={card.color} border={card.border} highlight={card.highlight} />
          ))}
        </div>
      </div>
    </div>
  );
}
